import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from './ui/dialog';
import { Loader2, User, Lock, Eye, EyeOff, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

export default function CompleteProfileModal() {
    const { user, token, requiresPasswordChange, requiresProfileUpdate, updateUser, refreshUser } = useAuth();
    const [open, setOpen] = useState(false);
    const [step, setStep] = useState(1); // 1 = profile, 2 = password
    const [saving, setSaving] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    
    // Form state
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');

    const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

    useEffect(() => {
        // Show modal if user needs to complete profile or change password
        if (user && (requiresProfileUpdate || requiresPasswordChange)) {
            setOpen(true);
            // Determine starting step
            if (requiresProfileUpdate) {
                setStep(1);
                setFirstName(user.first_name || '');
                setLastName(user.last_name || '');
            } else {
                setStep(2);
            }
        }
    }, [user, requiresProfileUpdate, requiresPasswordChange]);

    const handleProfileUpdate = async () => {
        if (!firstName.trim() || !lastName.trim()) {
            toast.error('Please enter your first and last name');
            return;
        }

        setSaving(true);
        try {
            const response = await fetch(`${API}/auth/profile`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    first_name: firstName.trim(),
                    last_name: lastName.trim()
                })
            });

            if (!response.ok) {
                let errorMessage = 'Failed to update profile';
                try {
                    const error = await response.json();
                    errorMessage = error.detail || errorMessage;
                } catch (e) {
                    // Response wasn't JSON
                }
                throw new Error(errorMessage);
            }

            toast.success('Profile updated!');
            updateUser({ 
                first_name: firstName.trim(), 
                last_name: lastName.trim(),
                requires_profile_update: false 
            });

            // Move to password step if needed, otherwise close
            if (requiresPasswordChange) {
                setStep(2);
            } else {
                setOpen(false);
                await refreshUser();
            }
        } catch (error) {
            toast.error(error.message);
        } finally {
            setSaving(false);
        }
    };

    const handlePasswordChange = async () => {
        if (!newPassword || newPassword.length < 6) {
            toast.error('Password must be at least 6 characters');
            return;
        }
        if (newPassword !== confirmPassword) {
            toast.error('Passwords do not match');
            return;
        }

        setSaving(true);
        try {
            const response = await fetch(`${API}/auth/change-password`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    new_password: newPassword
                })
            });

            if (!response.ok) {
                let errorMessage = 'Failed to change password';
                try {
                    const error = await response.json();
                    errorMessage = error.detail || errorMessage;
                } catch (e) {
                    // Response wasn't JSON
                }
                throw new Error(errorMessage);
            }

            toast.success('Password changed successfully!');
            updateUser({ requires_password_change: false });
            setOpen(false);
            await refreshUser();
        } catch (error) {
            toast.error(error.message);
        } finally {
            setSaving(false);
        }
    };

    if (!open) return null;

    const totalSteps = (requiresProfileUpdate ? 1 : 0) + (requiresPasswordChange ? 1 : 0);
    const currentStepNumber = requiresProfileUpdate ? step : 1;

    return (
        <Dialog open={open} onOpenChange={() => {}}>
            <DialogContent 
                className="bg-[#1A1A1A] border-[#333] text-white max-w-md"
                onPointerDownOutside={(e) => e.preventDefault()}
                onEscapeKeyDown={(e) => e.preventDefault()}
            >
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-3 text-xl">
                        {step === 1 ? (
                            <>
                                <div className="w-10 h-10 rounded-full bg-[#F51E1E]/20 flex items-center justify-center">
                                    <User className="w-5 h-5 text-[#F51E1E]" />
                                </div>
                                Complete Your Profile
                            </>
                        ) : (
                            <>
                                <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                                    <Lock className="w-5 h-5 text-amber-400" />
                                </div>
                                Set Your Password
                            </>
                        )}
                    </DialogTitle>
                    <DialogDescription className="text-[#A3A3A3] pt-2">
                        {step === 1 
                            ? "Welcome! Please tell us your name to get started."
                            : "Create a secure password for your account."
                        }
                    </DialogDescription>
                </DialogHeader>

                {/* Progress indicator */}
                {totalSteps > 1 && (
                    <div className="flex items-center gap-2 py-2">
                        <div className={`h-1 flex-1 rounded-full ${step >= 1 ? 'bg-[#F51E1E]' : 'bg-[#333]'}`} />
                        <div className={`h-1 flex-1 rounded-full ${step >= 2 ? 'bg-[#F51E1E]' : 'bg-[#333]'}`} />
                    </div>
                )}

                {step === 1 && requiresProfileUpdate ? (
                    <div className="space-y-4 py-4">
                        <div>
                            <Label className="text-white">First Name</Label>
                            <Input
                                value={firstName}
                                onChange={(e) => setFirstName(e.target.value)}
                                placeholder="Enter your first name"
                                className="mt-1 bg-[#262626] border-[#404040] text-white"
                                data-testid="profile-first-name"
                            />
                        </div>
                        <div>
                            <Label className="text-white">Last Name</Label>
                            <Input
                                value={lastName}
                                onChange={(e) => setLastName(e.target.value)}
                                placeholder="Enter your last name"
                                className="mt-1 bg-[#262626] border-[#404040] text-white"
                                data-testid="profile-last-name"
                            />
                        </div>
                        <Button
                            onClick={handleProfileUpdate}
                            disabled={saving || !firstName.trim() || !lastName.trim()}
                            className="w-full bg-[#F51E1E] hover:bg-[#D41212] text-white mt-4"
                            data-testid="save-profile-btn"
                        >
                            {saving ? (
                                <Loader2 className="animate-spin mr-2" size={18} />
                            ) : (
                                <CheckCircle className="mr-2" size={18} />
                            )}
                            {requiresPasswordChange ? 'Continue' : 'Save & Continue'}
                        </Button>
                    </div>
                ) : (
                    <div className="space-y-4 py-4">
                        <div>
                            <Label className="text-white">New Password</Label>
                            <div className="relative mt-1">
                                <Input
                                    type={showPassword ? 'text' : 'password'}
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    placeholder="Enter new password"
                                    className="bg-[#262626] border-[#404040] text-white pr-10"
                                    data-testid="new-password-input"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#666] hover:text-white"
                                >
                                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                                </button>
                            </div>
                            <p className="text-xs text-[#666] mt-1">At least 6 characters</p>
                        </div>
                        <div>
                            <Label className="text-white">Confirm Password</Label>
                            <div className="relative mt-1">
                                <Input
                                    type={showConfirmPassword ? 'text' : 'password'}
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    placeholder="Confirm new password"
                                    className="bg-[#262626] border-[#404040] text-white pr-10"
                                    data-testid="confirm-password-input"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#666] hover:text-white"
                                >
                                    {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                                </button>
                            </div>
                        </div>
                        {newPassword && confirmPassword && newPassword !== confirmPassword && (
                            <p className="text-red-400 text-sm">Passwords do not match</p>
                        )}
                        <Button
                            onClick={handlePasswordChange}
                            disabled={saving || !newPassword || newPassword.length < 6 || newPassword !== confirmPassword}
                            className="w-full bg-[#F51E1E] hover:bg-[#D41212] text-white mt-4"
                            data-testid="save-password-btn"
                        >
                            {saving ? (
                                <Loader2 className="animate-spin mr-2" size={18} />
                            ) : (
                                <CheckCircle className="mr-2" size={18} />
                            )}
                            Set Password & Continue
                        </Button>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
